import { gql, useSubscription, ApolloError } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { lobbyGameFragment } from '../../../graphql/fragments';
import {
  ActiveGames,
  FindNewGame,
  FindNewGameVariables,
  GameDifficulty,
  GameTypeEnum,
  OnlineOpponent,
} from '../../../graphql/types';
import { activeGamesQuery } from '../UnfinishedGames/GameList/GameList';

export type MatchmakingData = {
  startMatchmaking: (
    sceneId?: string,
    opponent?: OnlineOpponent,
    gameType?: GameTypeEnum,
  ) => void;
  stopMatchmaking: () => void;
  data?: FindNewGame;
  error?: ApolloError;
  loading: boolean;
  allowCpuMatch: boolean;
  opponent?: OnlineOpponent;
};

interface MatchmakingState {
  active: boolean;
  sceneId?: string;
  opponent?: OnlineOpponent;
  allowCpuMatch: boolean;
  gameType?: GameTypeEnum;
}

interface Props {
  gameDifficulty: GameDifficulty;
}

export default function useMatchmaking({ gameDifficulty }: Props) {
  const [matchmaking, setMatchmaking] = useState<MatchmakingState>({
    active: false,
    allowCpuMatch: false,
  });

  const timeoutRef = useRef<any>();

  const { error, data } = useSubscription<FindNewGame, FindNewGameVariables>(
    findNewGameSubscription,
    {
      skip: !matchmaking.active,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          sceneId: matchmaking.sceneId,
          opponentId: matchmaking.opponent?.user.id,
          gameType: matchmaking.gameType,
        },
      },
      onSubscriptionData({ client, subscriptionData: { data } }) {
        if (data == null) {
          return;
        }
        const { findNewGame: game } = data;
        const activeGamesData = client.readQuery<ActiveGames>({
          query: activeGamesQuery,
        });
        if (activeGamesData == null) {
          return;
        }
        const { activeGames } = activeGamesData;
        client.writeQuery({
          query: activeGamesQuery,
          data: { activeGames: activeGames.concat(game) },
        });
      },
      onSubscriptionComplete() {
        stopMatchmaking();
      },
    },
  );

  const startMatchmaking = useCallback(
    (sceneId?: string, opponent?: OnlineOpponent, gameType?: GameTypeEnum) => {
      setMatchmaking({
        sceneId,
        opponent,
        gameType,
        active: true,
        allowCpuMatch: false,
      });
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setMatchmaking((prev) => ({ ...prev, allowCpuMatch: true }));
      }, 10000);
    },
    [],
  );
  const stopMatchmaking = useCallback(() => {
    setMatchmaking({ active: false, allowCpuMatch: false });
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (matchmaking.active && gameDifficulty === GameDifficulty.EASY) {
      stopMatchmaking();
    }
  }, [matchmaking.active, gameDifficulty, stopMatchmaking]);

  return {
    startMatchmaking,
    stopMatchmaking,
    error,
    data,
    loading: matchmaking.active,
    allowCpuMatch: matchmaking.allowCpuMatch,
    opponent: matchmaking.opponent,
  };
}

const findNewGameSubscription = gql`
  subscription FindNewGame($input: FindNewGameInput!) {
    findNewGame(input: $input) {
      ...LobbyGame
    }
  }
  ${lobbyGameFragment}
`;
